(function () {
    'use strict';

    const layoutConfigs = {
        '01': {
            getSwiperOptions(itemsCount) {
                return {
                    slidesPerView: itemsCount > 1 ? 1.1 : 1,
                    breakpoints: {
                        744: { slidesPerView: itemsCount > 1 ? 1.25 : 1 },
                        1024: { slidesPerView: 2 },
                        1280: { slidesPerView: 3 },
                    }
                };
            }
        },
        '02': {
            getSwiperOptions() {
                return {
                    slidesPerView: 1,
                };
            }
        }
    };

    /**
     * Parse the badge titles passed from PHP.
     *
     * @param {HTMLElement} carousel
     * 
     * @returns {string[]}
     */
    function getBadgeTitles(carousel) {
        try {
            return JSON.parse(carousel.dataset.badgeTitles || '[]');
        } catch (error) {
            return [];
        }
    }

    /**
     * Collect the badge elements used by layout 02.
     *
     * @param {HTMLElement} carousel
     * 
     * @returns {{ badge: HTMLElement, current: HTMLElement, next: HTMLElement } | null}
     */
    function getBadgeElements(carousel) {
        const container = carousel.closest('.js-quote-carousel-container');
        const badge = container?.querySelector('.js-quote-carousel-badge');
        const current = badge?.querySelector('.js-quote-carousel-badge-current');
        const next = badge?.querySelector('.js-quote-carousel-badge-next');

        if (!badge || !current || !next) return null;

        return { badge, current, next };
    }

    /**
     * Measure the width needed to display a given badge title.
     * The text is written to a hidden line temporarily so we can
     * animate the outer badge without affecting the visible label.
     *
     * @param {HTMLElement} badge
     * @param {HTMLElement} textElement
     * @param {string} text
     * 
     * @returns {string}
     */
    function getBadgeWidth(badge, textElement, text) {
        const badgeStyles = window.getComputedStyle(badge);
        const paddingX = parseFloat(badgeStyles.paddingInline) * 2;
        const previousText = textElement.textContent;

        textElement.textContent = text || '';

        const width = text ? Math.ceil(textElement.scrollWidth + paddingX) : 0;

        textElement.textContent = previousText;

        return `${width}px`;
    }

    /**
     * Apply the settled badge state after an animation completes,
     * or immediately when no animation is needed.
     *
     * @param {{ badge: HTMLElement, current: HTMLElement, next: HTMLElement }} elements
     * @param {string} title
     * 
     * @returns {void}
     */
    function syncBadgeState(elements, title) {
        const { badge, current, next } = elements;

        badge.classList.add('is-resetting');
        badge.classList.remove('is-animating');
        badge.classList.toggle('is-empty', !title);

        current.textContent = title;
        next.textContent = '';
        badge.style.width = getBadgeWidth(badge, current, title);

        // Force the no-transition reset to apply before normal transitions resume.
        void badge.offsetWidth;
        badge.classList.remove('is-resetting');
        delete badge.dataset.nextTitle;
    }

    /**
     * Animate from the current badge title to the incoming one.
     *
     * @param {{ badge: HTMLElement, current: HTMLElement, next: HTMLElement }} elements
     * @param {string} title
     * 
     * @returns {void}
     */
    function animateBadgeState(elements, title) {
        const { badge, current, next } = elements;
        const currentWidth = `${badge.offsetWidth}px`;
        const nextWidth = getBadgeWidth(badge, next, title);

        badge.dataset.nextTitle = title;
        next.textContent = title;
        badge.style.width = currentWidth;
        badge.classList.toggle('is-empty', !title);

        requestAnimationFrame(() => {
            badge.classList.add('is-animating');
            badge.style.width = nextWidth;
        });

        const handleTransitionEnd = (event) => {
            if (event.target !== current || 'transform' !== event.propertyName) return;

            current.removeEventListener('transitionend', handleTransitionEnd);
            syncBadgeState(elements, title);
        };

        current.addEventListener('transitionend', handleTransitionEnd);
    }

    /**
     * Update the footer badge to match the active slide in layout 02.
     *
     * @param {HTMLElement} carousel
     * @param {Swiper} swiper
     * @param {{ animate?: boolean }} [options]
     * 
     * @returns {void}
     */
    function updateFooterBadge(carousel, swiper, options = {}) {
        if ('02' !== carousel.dataset.layout) return;

        const elements = getBadgeElements(carousel);

        if (!elements) return;

        const { animate = true } = options;
        const nextTitle = getBadgeTitles(carousel)[swiper.realIndex] || '';

        // Finish any in-flight animation first so repeated clicks do not stack transforms.
        if (elements.badge.dataset.nextTitle) {
            syncBadgeState(elements, elements.badge.dataset.nextTitle);
        }

        if (nextTitle === elements.current.textContent || !animate) {
            syncBadgeState(elements, nextTitle);
            return;
        }

        animateBadgeState(elements, nextTitle);
    }

    /**
     * Attach footer badge syncing to a carousel instance once Swiper is ready.
     *
     * @param {HTMLElement} carousel
     * 
     * @returns {void}
     */
    function bindFooterBadge(carousel) {
        if ('02' !== carousel.dataset.layout || !carousel.swiper || 'true' === carousel.dataset.badgeBound) {
            return;
        }

        carousel.dataset.badgeBound = 'true';

        const swiperInstance = carousel.swiper;

        // Set the initial badge title without playing the slide transition on page load.
        updateFooterBadge(carousel, carousel.swiper, { animate: false });
        carousel.swiper.on('slideChange', () => updateFooterBadge(carousel, swiperInstance));
    }

    function init() {
        if (!document.querySelector('.js-quote-carousel')) return;

        Object.entries(layoutConfigs).forEach(([layout, config]) => {
            const selector = `.js-quote-carousel[data-layout="${layout}"]`;
            const carousels = document.querySelectorAll(selector);

            if (!carousels.length) return;

            const itemsCount = carousels[0].style.getPropertyValue('--items-count');

            setupSwiper(selector, {
                containerSelector: '.js-quote-carousel-container',
                jsSelector: '.js-quote-carousel',
                swiperOptions: config.getSwiperOptions(itemsCount),
            });

            carousels.forEach(bindFooterBadge);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    document.addEventListener('facetwp-loaded', init);
})();